// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Platform Performance Panel
//
// Same feature set as the standalone repo's
// components/platform-performance/PlatformPerformancePanel.tsx: platform
// select, dynamic source-filter toggle (only shows filters present in the
// response), INC/RITM toggle, 12 KPI cards, and a custom trend graph with
// up to 4 selectable metrics (localStorage-persisted selection, same storage
// key), dual Y-axis when both count and percent/hours metrics are selected.
//
// Implementation substitution (framework/look change only, not a feature
// change): the old panel renders its trend chart with ECharts. This platform
// already uses Recharts in ten other components (ECharts isn't used
// anywhere here), so the chart is rebuilt with Recharts — same information,
// same interactivity (legend, tooltip, dual axis, area fill), different
// underlying chart library.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { Area, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AUTOMATION_PLATFORM_OPTIONS_WITH_ALL } from '../../config/automationPlatforms';
import { buildTimeFilterRequestParams, useAutomationFilters } from '../../contexts/AutomationFilterContext';
import { useAssignmentGroupScope } from '../../contexts/AssignmentGroupScopeContext';
import {
  AutomationBasis,
  AutomationTicketType,
  PlatformPerformanceKPIData,
  PlatformPerformanceResponse,
  PlatformPerformanceSourceFilterPayload,
  SourceFilterType,
  fetchAssignmentGroupsForPlatform,
  fetchPlatformPerformance,
} from '../../services/automationInsightsService';
import AutomationBreakdownTable from './AutomationBreakdownTable';

type TrendMetricKey =
  | 'vol_created'
  | 'vol_resolved'
  | 'auto_fulfilled_pct'
  | 'avg_mttr'
  | 'tori_vol'
  | 'cne_count'
  | 'cne_pct'
  | 'reopen_count'
  | 'reopen_pct';

type MetricUnit = 'count' | 'percent' | 'hours';

interface TrendMetricConfig {
  key: TrendMetricKey;
  label: string;
  unit: MetricUnit;
  color: string;
}

type FlexibleTrendRow = {
  week_start?: string;
  date?: string;
  month?: string;
} & Partial<Record<TrendMetricKey, number | null>>;

const SOURCE_FILTER_ORDER: SourceFilterType[] = ['all', 'automation', 'tori', 'human'];
const MAX_SELECTED_METRICS = 4;
// Same storage key as the standalone repo — different origin, no collision,
// kept identical for fidelity rather than renamed for its own sake.
const METRIC_SELECTION_STORAGE_KEY = 'platform-performance:custom-trend:selected-metrics:v1';
const DEFAULT_SELECTED_METRICS: TrendMetricKey[] = ['vol_created', 'vol_resolved', 'auto_fulfilled_pct'];

const TREND_METRIC_CONFIGS: TrendMetricConfig[] = [
  { key: 'vol_created', label: 'Volume Created', unit: 'count', color: '#0067b3' },
  { key: 'vol_resolved', label: 'Volume Resolved', unit: 'count', color: '#00a0dc' },
  { key: 'auto_fulfilled_pct', label: 'Auto Fulfilled %', unit: 'percent', color: '#00c48c' },
  { key: 'avg_mttr', label: 'Avg MTTR', unit: 'hours', color: '#ff6b35' },
  { key: 'tori_vol', label: 'TORI Volume', unit: 'count', color: '#7b61ff' },
  { key: 'cne_count', label: 'CNE Count', unit: 'count', color: '#f59e0b' },
  { key: 'cne_pct', label: 'CNE %', unit: 'percent', color: '#d97706' },
  { key: 'reopen_count', label: 'Reopen Count', unit: 'count', color: '#ef4444' },
  { key: 'reopen_pct', label: 'Reopen %', unit: 'percent', color: '#b91c1c' },
];

const KPI_FIELDS: Array<{ key: keyof PlatformPerformanceKPIData; label: string }> = [
  { key: 'vol_created', label: 'Volume Created' },
  { key: 'vol_resolved', label: 'Volume Resolved' },
  { key: 'auto_fulfilled', label: 'Auto Fulfilled' },
  { key: 'auto_fulfilled_pct', label: 'Auto Fulfilled %' },
  { key: 'tori_vol', label: 'TORI Volume' },
  { key: 'cne_count', label: 'CNE Count' },
  { key: 'cne_pct', label: 'CNE %' },
  { key: 'reopen_count', label: 'Reopen Count' },
  { key: 'reopen_pct', label: 'Reopen %' },
  { key: 'avg_mttr', label: 'Avg MTTR' },
  { key: 'avg_mttr_p50', label: 'MTTR P50' },
  { key: 'avg_mttr_p80', label: 'MTTR P80' },
  { key: 'avg_mttr_p85', label: 'MTTR P85' },
  { key: 'avg_mttr_p90', label: 'MTTR P90' },
];

function formatValue(value: number | null | undefined, field: keyof PlatformPerformanceKPIData): string {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
  if (field.includes('pct')) return `${value.toFixed(1)}%`;
  if (field.includes('mttr')) return `${value.toFixed(1)}h`;
  return value.toLocaleString();
}

function sourceFilterTitle(sourceFilter: SourceFilterType): string {
  if (sourceFilter === 'all') return 'All';
  if (sourceFilter === 'automation') return 'Automation';
  if (sourceFilter === 'human') return 'Human';
  return 'TORI';
}

function isTrendMetricKey(value: string): value is TrendMetricKey {
  return TREND_METRIC_CONFIGS.some((m) => m.key === value);
}

function formatTrendMetricValue(value: number, unit: MetricUnit): string {
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'hours') return `${value.toFixed(1)}h`;
  return value.toLocaleString();
}

export default function PlatformPerformancePanel() {
  const { filters } = useAutomationFilters();
  const { scope } = useAssignmentGroupScope();

  const [selectedPlatform, setSelectedPlatform] = useState('all');
  // Default 'INC' here, matching the old panel exactly — deliberately not
  // unified with Performance Alerts' default of 'RITM' above; each panel
  // keeps its own original default.
  const [selectedTicketType, setSelectedTicketType] = useState<AutomationTicketType>('INC');
  const [selectedSourceFilter, setSelectedSourceFilter] = useState<SourceFilterType>('all');
  // 'created' (default): every KPI/trend point reflects tickets CREATED in
  // that week. 'resolved': reflects tickets RESOLVED in that week instead —
  // a different cohort, not a recompute of the same one. See the tooltip on
  // the toggle below for the user-facing explanation. Added 2026-10,
  // RESOLVED_DATE_MIGRATION_PLAN.md §7.
  const [selectedBasis, setSelectedBasis] = useState<AutomationBasis>('created');
  const [selectedMetrics, setSelectedMetrics] = useState<TrendMetricKey[]>(DEFAULT_SELECTED_METRICS);
  const [data, setData] = useState<PlatformPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metricSelectionError, setMetricSelectionError] = useState<string | null>(null);

  // Assignment-group scoping (Stage 4). This dropdown's own options are
  // fetched for whatever platform is currently selected here; its value is
  // also kept in sync with Platform Summary's expand/select scope — a
  // one-way broadcast (per AssignmentGroupScopeContext's own doc comment),
  // so this panel never calls setScope back.
  const [selectedAssignmentGroup, setSelectedAssignmentGroup] = useState<string | null>(null);
  const [assignmentGroupOptions, setAssignmentGroupOptions] = useState<string[]>([]);
  const [assignmentGroupsLoading, setAssignmentGroupsLoading] = useState(false);

  const selectedMetricSet = useMemo(() => new Set(selectedMetrics), [selectedMetrics]);

  // Pick up Platform Summary's drill-down scope: expanding/selecting a
  // platform + group there switches this panel to match. Only acts when the
  // broadcast actually names a platform (an empty/default scope from
  // Platform Summary collapsing is not something Platform Performance should
  // reset itself to, since the user may already be looking at something
  // specific here).
  useEffect(() => {
    if (!scope.platform) return;
    setSelectedPlatform(scope.platform);
    setSelectedAssignmentGroup(scope.assignmentGroup);
  }, [scope.platform, scope.assignmentGroup]);

  // Assignment-group options for the currently selected platform. Not
  // available (and not meaningful) for the "all platforms" aggregate.
  useEffect(() => {
    if (selectedPlatform === 'all') {
      setAssignmentGroupOptions([]);
      setSelectedAssignmentGroup(null);
      return;
    }

    let cancelled = false;
    setAssignmentGroupsLoading(true);

    fetchAssignmentGroupsForPlatform(selectedPlatform, selectedTicketType)
      .then((response) => {
        if (cancelled) return;
        setAssignmentGroupOptions(response.groups ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setAssignmentGroupOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAssignmentGroupsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPlatform, selectedTicketType]);

  // Drop a previously selected group if it's no longer in the fetched list
  // for the (possibly new) platform/ticket-type combination.
  useEffect(() => {
    if (selectedAssignmentGroup && !assignmentGroupOptions.includes(selectedAssignmentGroup)) {
      setSelectedAssignmentGroup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentGroupOptions]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(METRIC_SELECTION_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const valid = parsed.filter((v): v is string => typeof v === 'string').filter(isTrendMetricKey);
      if (valid.length > 0) setSelectedMetrics(valid.slice(0, MAX_SELECTED_METRICS));
    } catch {
      // ignore corrupted storage, same as old panel
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(METRIC_SELECTION_STORAGE_KEY, JSON.stringify(selectedMetrics));
    } catch {
      // ignore storage permission failures
    }
  }, [selectedMetrics]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeParams = buildTimeFilterRequestParams(filters);

    fetchPlatformPerformance({
      platform: selectedPlatform,
      ticketType: selectedTicketType,
      sourceFilter: 'all',
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      assignmentGroup: selectedAssignmentGroup ?? undefined,
      basis: selectedBasis,
    })
      .then((response) => {
        if (cancelled) return;
        setData(response);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load platform performance data');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPlatform, selectedTicketType, selectedAssignmentGroup, selectedBasis, filters]);

  useEffect(() => {
    if (!data?.source_filters) return;
    if (data.source_filters[selectedSourceFilter]) return;
    const fallback = SOURCE_FILTER_ORDER.find((key) => Boolean(data.source_filters[key]));
    if (fallback) setSelectedSourceFilter(fallback);
  }, [data, selectedSourceFilter]);

  const activeSourceFilters = useMemo(() => {
    if (!data?.source_filters) return [] as SourceFilterType[];
    return SOURCE_FILTER_ORDER.filter((key) => Boolean(data.source_filters[key]));
  }, [data]);

  const getTrendRows = (sourceData: PlatformPerformanceSourceFilterPayload | undefined): FlexibleTrendRow[] => {
    if (!sourceData) return [];
    if (filters.timeFilterType === 'day') return (sourceData.daily_trend as FlexibleTrendRow[]) ?? sourceData.weekly_trend ?? [];
    if (filters.timeFilterType === 'month') return (sourceData.monthly_trend as FlexibleTrendRow[]) ?? sourceData.weekly_trend ?? [];
    return sourceData.weekly_trend ?? [];
  };

  const getXValue = (row: FlexibleTrendRow): string => {
    if (filters.timeFilterType === 'day') return row.date ?? row.week_start ?? row.month ?? '';
    if (filters.timeFilterType === 'month') return row.month ?? row.week_start ?? row.date ?? '';
    return row.week_start ?? row.date ?? row.month ?? '';
  };

  const getMetricAvailability = (rows: FlexibleTrendRow[]): Record<TrendMetricKey, boolean> =>
    TREND_METRIC_CONFIGS.reduce((acc, m) => {
      acc[m.key] = rows.some((row) => row[m.key] !== undefined && row[m.key] !== null);
      return acc;
    }, {} as Record<TrendMetricKey, boolean>);

  const handleMetricToggle = (metricKey: TrendMetricKey, checked: boolean) => {
    setMetricSelectionError(null);
    setSelectedMetrics((previous) => {
      const sourceData = data?.source_filters[selectedSourceFilter];
      const rows = getTrendRows(sourceData);
      const availability = getMetricAvailability(rows);

      if (checked) {
        if (previous.includes(metricKey)) return previous;
        if (!availability[metricKey]) {
          setMetricSelectionError('Selected metric is not available in the current API trend payload.');
          return previous;
        }
        if (previous.length >= MAX_SELECTED_METRICS) {
          setMetricSelectionError(`You can select up to ${MAX_SELECTED_METRICS} metrics.`);
          return previous;
        }
        return [...previous, metricKey];
      }

      const next = previous.filter((m) => m !== metricKey);
      if (next.length === 0) {
        setMetricSelectionError('Select at least one metric to render the trend chart.');
        return previous;
      }
      return next;
    });
  };

  const resetMetricsToDefaults = () => {
    setMetricSelectionError(null);
    setSelectedMetrics(DEFAULT_SELECTED_METRICS);
  };

  const sourceData = data?.source_filters[selectedSourceFilter];
  const trendRows = getTrendRows(sourceData);
  const metricAvailability = getMetricAvailability(trendRows);
  const selectedConfigs = TREND_METRIC_CONFIGS.filter((m) => selectedMetricSet.has(m.key));
  const hasCountMetrics = selectedConfigs.some((m) => m.unit === 'count');
  const hasRightAxisMetrics = selectedConfigs.some((m) => m.unit !== 'count');

  const chartData = useMemo(
    () =>
      trendRows.map((row) => {
        const point: Record<string, string | number | null> = { x: getXValue(row) };
        selectedConfigs.forEach((metric) => {
          point[metric.key] = row[metric.key] ?? null;
        });
        return point;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trendRows, selectedConfigs]
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} gap={2} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          Platform Performance
        </Typography>

        <Stack direction="row" spacing={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="pp-platform-label">Platform</InputLabel>
            <Select
              labelId="pp-platform-label"
              label="Platform"
              value={selectedPlatform}
              onChange={(e: SelectChangeEvent) => setSelectedPlatform(e.target.value)}
            >
              {AUTOMATION_PLATFORM_OPTIONS_WITH_ALL.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTooltip
            title={
              selectedBasis === 'created'
                ? 'Created: every number reflects tickets CREATED in the selected week(s) — the current/default view. A ticket created this week keeps counting here even if it resolves weeks later.'
                : 'Resolved: every number reflects tickets RESOLVED in the selected week(s), regardless of when they were created. This is a different set of tickets than the Created view, not the same tickets viewed differently.'
            }
            arrow
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedBasis}
              onChange={(_e, v: AutomationBasis | null) => v && setSelectedBasis(v)}
            >
              <ToggleButton value="created">Created</ToggleButton>
              <ToggleButton value="resolved">Resolved</ToggleButton>
            </ToggleButtonGroup>
          </MuiTooltip>

          {selectedPlatform !== 'all' ? (
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={assignmentGroupsLoading || assignmentGroupOptions.length === 0}>
              <InputLabel id="pp-assignment-group-label">Assignment group</InputLabel>
              <Select
                labelId="pp-assignment-group-label"
                label="Assignment group"
                value={selectedAssignmentGroup ?? ''}
                onChange={(e: SelectChangeEvent) => setSelectedAssignmentGroup(e.target.value || null)}
              >
                <MenuItem value="">All groups</MenuItem>
                {assignmentGroupOptions.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {activeSourceFilters.length > 0 ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={selectedSourceFilter}
              onChange={(_e, v: SourceFilterType | null) => v && setSelectedSourceFilter(v)}
            >
              {activeSourceFilters.map((sf) => (
                <ToggleButton key={sf} value={sf}>
                  {sourceFilterTitle(sf)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : null}

          <ToggleButtonGroup
            size="small"
            exclusive
            value={selectedTicketType}
            onChange={(_e, v: AutomationTicketType | null) => v && setSelectedTicketType(v)}
          >
            <ToggleButton value="INC">INC</ToggleButton>
            <ToggleButton value="RITM">SR</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            {KPI_FIELDS.map((f) => (
              <Grid item xs={6} sm={4} md={2} key={String(f.key)}>
                <Card variant="outlined">
                  <CardContent>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" height={32} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
        </Stack>
      ) : !sourceData ? (
        <Alert severity="warning">No platform performance summary available.</Alert>
      ) : (
        <Stack spacing={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            {sourceFilterTitle(selectedSourceFilter)}
          </Typography>

          <Grid container spacing={2}>
            {KPI_FIELDS.map((field) => (
              <Grid item xs={6} sm={4} md={2} key={String(field.key)}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      {field.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {formatValue(sourceData.kpis[field.key], field.key)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                Custom Trend Graph
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Selected: {selectedMetrics.length}/{MAX_SELECTED_METRICS}
                </Typography>
                <Button size="small" variant="outlined" onClick={resetMetricsToDefaults}>
                  Reset defaults
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={1}>
              {TREND_METRIC_CONFIGS.map((metric) => (
                <Grid item xs={6} sm={4} md={3} key={metric.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedMetricSet.has(metric.key)}
                        disabled={!metricAvailability[metric.key]}
                        onChange={(e) => handleMetricToggle(metric.key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{metric.label}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>

            {metricSelectionError ? (
              <Typography variant="caption" color="error" display="block" mt={0.5}>
                {metricSelectionError}
              </Typography>
            ) : null}

            {TREND_METRIC_CONFIGS.some((m) => selectedMetricSet.has(m.key) && !metricAvailability[m.key]) ? (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Some selected metrics are disabled until the backend trend payload includes those series.
              </Typography>
            ) : null}

            {trendRows.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No trend rows are available for the selected time granularity and filters.
              </Alert>
            ) : (
              <Box sx={{ width: '100%', height: 320, mt: 1 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="x" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    {hasCountMetrics && hasRightAxisMetrics ? (
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    ) : null}
                    <Tooltip
                      formatter={(value: any, name: any) => {
                        const metric = TREND_METRIC_CONFIGS.find((m) => m.label === name);
                        if (value === null || value === undefined) return ['—', name];
                        return [metric ? formatTrendMetricValue(Number(value), metric.unit) : value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {selectedConfigs.map((metric) => {
                      const useRightAxis = hasCountMetrics && hasRightAxisMetrics && metric.unit !== 'count';
                      return (
                        <Area
                          key={metric.key}
                          type="monotone"
                          dataKey={metric.key}
                          name={metric.label}
                          yAxisId={useRightAxis ? 'right' : 'left'}
                          stroke={metric.color}
                          fill={metric.color}
                          fillOpacity={0.12}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Stack>
      )}

      <AutomationBreakdownTable
        platform={selectedPlatform}
        ticketType={selectedTicketType}
        sourceFilter={selectedSourceFilter}
        basis={selectedBasis}
        filters={filters}
      />
    </Box>
  );
}
